import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ordensApi, clientesApi, colaboradoresApi, servicosApi, cidadesApi, pixApi } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, STATUS_OPTIONS, FORMAS_PAGAMENTO, PERIODOS_ATENDIMENTO, generateWhatsAppMessageCliente, generateWhatsAppMessageColaborador, generateWhatsAppMessageConsultaPrestador, copyToClipboard, formatEndereco, formatOrdemNumero } from '../lib/utils';
import { CidadeSelectOptions, ZonaBairroFields } from '../components/CidadeSelectOptions';
import { ColaboradorCombobox } from '../components/ColaboradorCombobox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ServicoPicker } from '../components/ui/ServicoPicker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Loader2, Calendar, Copy, MessageCircle, Eye, Sparkles, ChevronDown, FileDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const emptyItem = () => ({ servico_id: '', servico_nome: '', quantidade: 1, valor_unitario: 0 });

const emptyEndereco = { rua: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '', cep: '' };

const emptyOrdem = {
  cliente_id: '',
  colaborador_id: '',
  itens: [emptyItem()],
  data_agendamento: '',
  hora_agendamento: '',
  status: 'Agendado',
  forma_pagamento: '',
  observacoes: '',
  periodo_atendimento: '',
  cidade_atendimento: '',
  zona_atendimento: '',
  bairro_atendimento: '',
  valor_prestador: ''
};

const Agendamento = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [ordens, setOrdens] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [cidadesList, setCidadesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedOrdem, setSelectedOrdem] = useState(null);
  const [formData, setFormData] = useState({ ...emptyOrdem });
  const [saving, setSaving] = useState(false);
  const [pendingOrcamentoId, setPendingOrcamentoId] = useState(null);
  const [vpTouched, setVpTouched] = useState(false);
  const [enderecoForm, setEnderecoForm] = useState({ ...emptyEndereco });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [ordensRes, clientesRes, colaboradoresRes, servicosRes, cidadesRes] = await Promise.all([
        ordensApi.list(periodo),
        clientesApi.list(),
        colaboradoresApi.list(),
        servicosApi.list(),
        cidadesApi.list()
      ]);
      setOrdens(ordensRes.data);
      setClientes(clientesRes.data);
      setColaboradores(colaboradoresRes.data.filter((c) => c.ativo !== false));
      setServicos(servicosRes.data);
      setCidadesList(cidadesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-fill valor_prestador with default suggested value (comissao% × total)
  // unless the user has manually touched the field.
  useEffect(() => {
    if (!showForm || vpTouched) return;
    const colab = colaboradores.find((c) => c.id === formData.colaborador_id);
    const pct = colab ? Number(colab.comissao || 0) : 0;
    const total = formData.itens.reduce(
      (sum, it) => sum + (Number(it.valor_unitario || 0) * Number(it.quantidade || 1)),
      0
    );
    if (pct > 0 && total > 0) {
      const sugerido = (total * pct / 100).toFixed(2);
      if (formData.valor_prestador !== sugerido) {
        setFormData((prev) => ({ ...prev, valor_prestador: sugerido }));
      }
    } else if (formData.valor_prestador !== '') {
      setFormData((prev) => ({ ...prev, valor_prestador: '' }));
    }
  }, [showForm, vpTouched, formData.colaborador_id, formData.itens, colaboradores, formData.valor_prestador]);

  // Match automático de prestadores: categoria + cidade + zona.
  // Prioridade 1: atende a zona específica; Prioridade 2: atende "toda a cidade".
  const suggestedColaboradores = useMemo(() => {
    if (!showForm) return [];
    const cidade = (formData.cidade_atendimento || '').trim().toLowerCase();
    const zona = (formData.zona_atendimento || '').trim().toLowerCase();
    const categoriasNecessarias = new Set(
      formData.itens
        .map((it) => {
          const s = servicos.find((sv) => sv.id === it.servico_id);
          return s?.categoria_nome ? String(s.categoria_nome).trim().toLowerCase() : '';
        })
        .filter(Boolean)
    );
    if (!cidade && categoriasNecessarias.size === 0) return [];
    const atende = (c) => {
      if (!cidade) return { ok: true, prioridade: 2 };
      const areas = c.areas_atendimento || [];
      if (areas.length > 0) {
        let best = null;
        for (const a of areas) {
          if (String(a.cidade || '').trim().toLowerCase() !== cidade) continue;
          const zonasArea = (a.zonas || []).map((z) => String(z).trim().toLowerCase());
          if (a.toda_cidade || zonasArea.length === 0) {
            best = best ? Math.min(best, 2) : 2;
          } else if (!zona || zonasArea.includes(zona)) {
            best = 1;
          }
        }
        return best ? { ok: true, prioridade: best } : { ok: false, prioridade: 0 };
      }
      // Legado: lista de cidades = atende a cidade toda
      const regioes = (c.regioes_atendimento || []).map((r) => String(r).trim().toLowerCase());
      return regioes.includes(cidade) ? { ok: true, prioridade: 2 } : { ok: false, prioridade: 0 };
    };
    return colaboradores
      .map((c) => {
        const { ok, prioridade } = atende(c);
        const cats = (c.categorias_servico || []).map((k) => String(k).trim().toLowerCase());
        const catsCobertas = [...categoriasNecessarias].filter((cat) => cats.includes(cat));
        return { c, cidadeOk: ok, prioridade, catsCobertas, cover: catsCobertas.length };
      })
      .filter((x) => x.cidadeOk && (categoriasNecessarias.size === 0 || x.cover > 0))
      .sort((a, b) => (a.prioridade - b.prioridade) || (b.cover - a.cover))
      .slice(0, 5);
  }, [showForm, formData.cidade_atendimento, formData.zona_atendimento, formData.itens, colaboradores, servicos]);

  // Prefill endereço a partir do cadastro do cliente sempre que o cliente mudar
  useEffect(() => {
    if (!showForm) return;
    if (!formData.cliente_id) {
      setEnderecoForm({ ...emptyEndereco });
      return;
    }
    const cli = clientes.find((c) => c.id === formData.cliente_id);
    setEnderecoForm({ ...emptyEndereco, ...(cli?.endereco || {}) });
  }, [showForm, formData.cliente_id, clientes]);

  // Auto-open pre-filled form when navigated from Orçamento "Fechar" action
  useEffect(() => {
    const fromOrc = location.state?.fromOrcamento;
    if (fromOrc && !loading && clientes.length > 0) {
      const today = new Date();
      const brasiliaDate = today.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
      const obsParts = [];
      if (fromOrc.observacoes) obsParts.push(fromOrc.observacoes);
      // Default hora based on período
      let defaultHora = '';
      if (fromOrc.periodo_atendimento === 'Manhã') defaultHora = '08:00';
      else if (fromOrc.periodo_atendimento === 'Tarde') defaultHora = '13:00';
      // Build itens: use fromOrc.itens if present, else synthesize from legacy fields
      let itens = [emptyItem()];
      if (Array.isArray(fromOrc.itens) && fromOrc.itens.length > 0) {
        itens = fromOrc.itens.map((it) => ({
          servico_id: it.servico_id || '',
          servico_nome: it.servico_nome || '',
          quantidade: Number(it.quantidade || 1),
          valor_unitario: Number(it.valor_unitario || 0)
        }));
      } else if (fromOrc.servico_id) {
        itens = [{
          servico_id: fromOrc.servico_id,
          servico_nome: fromOrc.descricao_servico || '',
          quantidade: 1,
          valor_unitario: Number(fromOrc.valor || 0)
        }];
      }
      setSelectedOrdem(null);
      setFormData({
        ...emptyOrdem,
        cliente_id: fromOrc.cliente_id || '',
        itens,
        data_agendamento: brasiliaDate,
        hora_agendamento: defaultHora,
        cidade_atendimento: fromOrc.cidade_atendimento || '',
        zona_atendimento: fromOrc.zona_atendimento || '',
        bairro_atendimento: fromOrc.bairro_atendimento || '',
        periodo_atendimento: fromOrc.periodo_atendimento || '',
        observacoes: obsParts.join(' | ')
      });
      setPendingOrcamentoId(fromOrc.orcamento_id || null);
      setShowForm(true);
      navigate(location.pathname, { replace: true, state: null });
      toast.info('Formulário preenchido com dados do orçamento');
    }
  }, [location.state, loading, clientes, navigate, location.pathname]);

  const handleOpenForm = (ordem = null) => {
    if (ordem) {
      setSelectedOrdem(ordem);
      // Build itens from ordem
      let itens = [emptyItem()];
      if (Array.isArray(ordem.itens) && ordem.itens.length > 0) {
        itens = ordem.itens.map((it) => ({
          servico_id: it.servico_id || '',
          servico_nome: it.servico_nome || '',
          quantidade: Number(it.quantidade || 1),
          valor_unitario: Number(it.valor_unitario || 0)
        }));
      } else if (ordem.servico_id) {
        itens = [{
          servico_id: ordem.servico_id,
          servico_nome: ordem.servico_nome || '',
          quantidade: 1,
          valor_unitario: Number(ordem.valor || 0)
        }];
      }
      setFormData({
        cliente_id: ordem.cliente_id,
        colaborador_id: ordem.colaborador_id,
        itens,
        data_agendamento: ordem.data_agendamento,
        hora_agendamento: ordem.hora_agendamento,
        status: ordem.status,
        forma_pagamento: ordem.forma_pagamento || '',
        observacoes: ordem.observacoes || '',
        periodo_atendimento: ordem.periodo_atendimento || '',
        cidade_atendimento: ordem.cidade_atendimento || '',
        zona_atendimento: ordem.zona_atendimento || '',
        bairro_atendimento: ordem.bairro_atendimento || '',
        valor_prestador: ordem.valor_prestador != null ? String(ordem.valor_prestador) : ''
      });
      // Existing OS with saved valor_prestador is considered "touched" so we don't override
      setVpTouched(ordem.valor_prestador != null);
    } else {
      setSelectedOrdem(null);
      const today = new Date();
      const brasiliaDate = today.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
      setFormData({ 
        ...emptyOrdem,
        itens: [emptyItem()],
        data_agendamento: brasiliaDate
      });
      setVpTouched(false);
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedOrdem(null);
    setFormData({ ...emptyOrdem, itens: [emptyItem()] });
    setPendingOrcamentoId(null);
    setVpTouched(false);
    setEnderecoForm({ ...emptyEndereco });
  };

  const updateEndereco = (field, value) => {
    setEnderecoForm((prev) => ({ ...prev, [field]: value }));
  };

  // Update a single item at index
  const updateItem = (idx, patch) => {
    setFormData((prev) => {
      const nextItens = prev.itens.map((it, i) => (i === idx ? { ...it, ...patch } : it));
      return { ...prev, itens: nextItens };
    });
  };

  const handleItemServicoChange = (idx, servicoId) => {
    const servico = servicos.find((s) => s.id === servicoId);
    updateItem(idx, {
      servico_id: servicoId,
      servico_nome: servico?.nome || '',
      valor_unitario: servico?.preco_base || 0
    });
  };

  const addItem = () => {
    setFormData((prev) => ({ ...prev, itens: [...prev.itens, emptyItem()] }));
  };

  const removeItem = (idx) => {
    setFormData((prev) => {
      const nextItens = prev.itens.filter((_, i) => i !== idx);
      return { ...prev, itens: nextItens.length ? nextItens : [emptyItem()] };
    });
  };

  const computeTotal = () => {
    return formData.itens.reduce(
      (sum, it) => sum + (Number(it.valor_unitario || 0) * Number(it.quantidade || 1)),
      0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.cliente_id) {
      toast.error('Selecione o cliente');
      return;
    }
    const itensValidos = formData.itens.filter((it) => it.servico_id);
    if (itensValidos.length === 0) {
      toast.error('Adicione ao menos 1 item com serviço selecionado');
      return;
    }

    setSaving(true);

    try {
      // Atualiza o endereço no cadastro do cliente antes de salvar a OS
      // (o backend copia o endereço do cliente para a OS)
      await clientesApi.update(formData.cliente_id, { endereco: enderecoForm });

      const { valor: _legacy, servico_id: _legacyS, valor_prestador: vpStr, ...rest } = formData;
      const parsedVp = vpStr === '' || vpStr === null || vpStr === undefined ? null : Number(vpStr);
      const payload = {
        ...rest,
        valor_prestador: (parsedVp != null && !isNaN(parsedVp)) ? parsedVp : null,
        itens: itensValidos.map((it) => ({
          servico_id: it.servico_id,
          servico_nome: it.servico_nome,
          quantidade: Math.max(0.01, Number(it.quantidade || 1)),
          valor_unitario: Number(it.valor_unitario || 0)
        }))
      };
      if (selectedOrdem) {
        await ordensApi.update(selectedOrdem.id, payload);
        toast.success('Ordem de serviço atualizada!');
      } else {
        const finalPayload = pendingOrcamentoId
          ? { ...payload, orcamento_id: pendingOrcamentoId }
          : payload;
        await ordensApi.create(finalPayload);
        toast.success(pendingOrcamentoId
          ? 'OS criada e orçamento marcado como Aprovado!'
          : 'Ordem de serviço criada!');
        setPendingOrcamentoId(null);
      }
      handleCloseForm();
      loadData();
    } catch (error) {
      console.error('Error saving ordem:', error);
      toast.error('Erro ao salvar ordem de serviço');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedOrdem) return;

    try {
      await ordensApi.delete(selectedOrdem.id);
      toast.success('Ordem de serviço excluída!');
      setShowDeleteDialog(false);
      setShowDetails(false);
      setSelectedOrdem(null);
      loadData();
    } catch (error) {
      console.error('Error deleting ordem:', error);
      toast.error('Erro ao excluir ordem de serviço');
    }
  };

  const baixarRecibo = async (ordem) => {
    try {
      const r = await ordensApi.reciboPdf(ordem.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Recibo-${formatOrdemNumero(ordem.numero)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao gerar recibo');
    }
  };

  const handleCopyWhatsApp = async (ordem, type) => {
    let message;
    if (type === 'cliente') {
      let pixLink = '';
      try {
        const res = await pixApi.cobrancaForOrdem(ordem.id);
        pixLink = `${window.location.origin}/pagar/${res.data.id}`;
      } catch (error) {
        const detail = error?.response?.data?.detail;
        toast.warning(detail
          ? `Mensagem sem link PIX: ${detail}`
          : 'Mensagem copiada sem link PIX (erro ao gerar cobrança)');
      }
      message = generateWhatsAppMessageCliente({ ...ordem, pix_link: pixLink });
    } else if (type === 'consulta') {
      message = generateWhatsAppMessageConsultaPrestador(ordem);
    } else {
      message = generateWhatsAppMessageColaborador(ordem);
    }
    
    const success = await copyToClipboard(message);
    if (success) {
      toast.success(type === 'consulta' ? 'Mensagem de consulta copiada! Preencha o valor antes de enviar.' : `Mensagem para ${type} copiada!`);
    } else {
      toast.error('Erro ao copiar mensagem');
    }
  };

  const handleUpdateStatus = async (ordemId, newStatus) => {
    try {
      await ordensApi.update(ordemId, { status: newStatus });
      toast.success('Status atualizado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const [onlySemColab, setOnlySemColab] = useState(false);

  const semColabCount = ordens.filter((o) => !o.colaborador_id).length;
  const filteredOrdens = ordens.filter(ordem => {
    if (onlySemColab && ordem.colaborador_id) return false;
    const q = searchTerm.toLowerCase();
    return (
      (ordem.cliente_nome || '').toLowerCase().includes(q) ||
      (ordem.colaborador_nome || '').toLowerCase().includes(q) ||
      (ordem.servico_nome || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="agendamento-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
            Agendamento
          </h1>
          <p className="text-gray-500 mt-1">
            Gerencie suas ordens de serviço
          </p>
        </div>
        <Button onClick={() => handleOpenForm()} data-testid="add-ordem-btn">
          <Plus size={18} className="mr-2" />
          Nova Ordem de Serviço
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
              <Calendar size={20} />
              Ordens de Serviço ({filteredOrdens.length})
            </CardTitle>
            <div className="flex items-center gap-2 w-full md:w-auto">
              <Button
                type="button"
                variant={onlySemColab ? 'default' : 'outline'}
                onClick={() => setOnlySemColab(!onlySemColab)}
                className={onlySemColab ? 'text-white' : ''}
                style={onlySemColab ? { backgroundColor: '#B45309' } : {}}
                data-testid="filtro-sem-colab"
                title="Filtrar OS sem colaborador escalado"
              >
                Sem colaborador
                {semColabCount > 0 && (
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${onlySemColab ? 'bg-white/25' : 'bg-amber-100 text-amber-700'}`}>
                    {semColabCount}
                  </span>
                )}
              </Button>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-ordem-input"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={periodo} onValueChange={setPeriodo} className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="dia" data-testid="filter-dia">Hoje</TabsTrigger>
              <TabsTrigger value="semana" data-testid="filter-semana">Semana</TabsTrigger>
              <TabsTrigger value="mes" data-testid="filter-mes">Mês</TabsTrigger>
              <TabsTrigger value="all" data-testid="filter-all">Todas</TabsTrigger>
            </TabsList>

            <TabsContent value={periodo}>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : filteredOrdens.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ? 'Nenhuma ordem encontrada' : 'Nenhuma ordem de serviço'}
                </div>
              ) : (
                <div className="table-fit">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Nº OS</th>
                        <th>Data</th>
                        <th className="hidden lg:table-cell">Hora</th>
                        <th>Cliente</th>
                        <th className="hidden lg:table-cell">Serviço</th>
                        <th className="hidden xl:table-cell">Colaborador</th>
                        <th>Valor</th>
                        <th>Status</th>
                        <th className="text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrdens.map((ordem) => (
                        <tr key={ordem.id} data-testid={`ordem-row-${ordem.id}`} className={!ordem.colaborador_id ? 'bg-amber-50/60' : ''}>
                          <td className="font-mono text-xs">
                            {ordem.numero ? (
                              <span className="px-2 py-1 rounded bg-gray-100 border font-semibold text-gray-700">
                                {formatOrdemNumero(ordem.numero)}
                              </span>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                          <td>{formatDate(ordem.data_agendamento)}</td>
                          <td className="hidden lg:table-cell">{ordem.hora_agendamento}</td>
                          <td className="font-medium">
                            {ordem.cliente_nome}
                            {ordem.origem === 'parceiro' && (
                              <span className="ml-1.5 inline-flex flex-col align-middle">
                                <span className="text-[9px] font-bold text-white bg-[#2B7A3D] px-1.5 py-0.5 rounded leading-tight" data-testid={`badge-parceiro-${ordem.id}`} title={`Parceiro: ${ordem.parceiro_nome}${ordem.parceiro_usuario_nome ? ' · Solicitado por: ' + ordem.parceiro_usuario_nome : ''}`}>
                                  PARCEIRO
                                </span>
                              </span>
                            )}
                            {ordem.origem === 'parceiro' && (
                              <div className="text-[10px] text-gray-400 font-normal leading-tight">
                                {ordem.parceiro_nome}{ordem.parceiro_usuario_nome ? ` · ${ordem.parceiro_usuario_nome}` : ''}
                                {ordem.aprovacao_parceiro === 'aguardando_valor' && <span className="text-amber-600 font-semibold"> · definir valor</span>}
                                {ordem.aprovacao_parceiro === 'aguardando_aprovacao' && <span className="text-blue-600 font-semibold"> · aguardando aprovação do parceiro</span>}
                                {ordem.prioridade === 'urgente' && <span className="text-red-600 font-semibold"> · URGENTE</span>}
                              </div>
                            )}
                          </td>
                          <td className="cell-ellipsis hidden lg:table-cell" title={ordem.servico_nome}>{ordem.servico_nome}</td>
                          <td className="hidden xl:table-cell">
                            {ordem.colaborador_id ? (
                              ordem.colaborador_nome
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800" data-testid={`badge-sem-colab-${ordem.id}`}>
                                ⏳ Escalar depois
                              </span>
                            )}
                          </td>
                          <td>{formatCurrency(ordem.valor)}</td>
                          <td>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button type="button" data-testid={`status-quick-${ordem.id}`} title="Clique para mudar o status">
                                  <Badge
                                    className="cursor-pointer hover:opacity-80 inline-flex items-center gap-1"
                                    style={{ backgroundColor: getStatusColor(ordem.status), color: 'white' }}
                                  >
                                    {ordem.status}
                                    <ChevronDown size={11} />
                                  </Badge>
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                {STATUS_OPTIONS.filter((s) => s !== ordem.status).map((s) => (
                                  <DropdownMenuItem
                                    key={s}
                                    data-testid={`status-opt-${ordem.id}-${s}`}
                                    onClick={() => handleUpdateStatus(ordem.id, s)}
                                  >
                                    <span
                                      className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                                      style={{ backgroundColor: getStatusColor(s) }}
                                    />
                                    {s}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                          <td>
                            <div className="flex items-center gap-1 flex-nowrap justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedOrdem(ordem);
                                  setShowDetails(true);
                                }}
                                data-testid={`view-ordem-${ordem.id}`}
                              >
                                <Eye size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenForm(ordem)}
                                data-testid={`edit-ordem-${ordem.id}`}
                              >
                                <Pencil size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedOrdem(ordem);
                                  setShowDeleteDialog(true);
                                }}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`delete-ordem-${ordem.id}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCopyWhatsApp(ordem, 'cliente')}
                                title="Copiar para WhatsApp"
                                data-testid={`whatsapp-ordem-${ordem.id}`}
                              >
                                <MessageCircle size={16} className="text-green-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>
              {selectedOrdem ? 'Editar Ordem de Serviço' : 'Nova Ordem de Serviço'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados da ordem de serviço
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cliente_id">Cliente *</Label>
              <Select 
                value={formData.cliente_id} 
                onValueChange={(value) => setFormData({ ...formData, cliente_id: value })}
              >
                <SelectTrigger data-testid="cliente-select">
                  <SelectValue placeholder="Selecione o cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map((cliente) => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.nome} - {cliente.telefone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.cliente_id && (
              <div className="space-y-2 rounded-lg border p-3 bg-gray-50" data-testid="endereco-section">
                <Label className="font-semibold">Endereço do Cliente</Label>
                <p className="text-xs text-gray-500">Editar aqui atualiza o cadastro do cliente e a OS.</p>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    className="col-span-2"
                    placeholder="Rua"
                    value={enderecoForm.rua}
                    onChange={(e) => updateEndereco('rua', e.target.value)}
                    data-testid="endereco-rua"
                  />
                  <Input
                    placeholder="Número"
                    value={enderecoForm.numero}
                    onChange={(e) => updateEndereco('numero', e.target.value)}
                    data-testid="endereco-numero"
                  />
                  <Input
                    placeholder="Complemento"
                    value={enderecoForm.complemento}
                    onChange={(e) => updateEndereco('complemento', e.target.value)}
                    data-testid="endereco-complemento"
                  />
                  <Input
                    placeholder="Bairro"
                    value={enderecoForm.bairro}
                    onChange={(e) => updateEndereco('bairro', e.target.value)}
                    data-testid="endereco-bairro"
                  />
                  <Input
                    placeholder="CEP"
                    value={enderecoForm.cep}
                    onChange={(e) => updateEndereco('cep', e.target.value)}
                    data-testid="endereco-cep"
                  />
                  <Input
                    className="col-span-2"
                    placeholder="Cidade"
                    value={enderecoForm.cidade}
                    onChange={(e) => updateEndereco('cidade', e.target.value)}
                    data-testid="endereco-cidade"
                  />
                  <Input
                    placeholder="UF"
                    maxLength={2}
                    value={enderecoForm.estado}
                    onChange={(e) => updateEndereco('estado', e.target.value.toUpperCase())}
                    data-testid="endereco-estado"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="colaborador_id">Colaborador (opcional — pode escalar depois)</Label>
              {suggestedColaboradores.length > 0 && (
                <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-2" data-testid="colab-sugestoes">
                  <div className="flex items-center gap-1 text-xs text-emerald-800 font-medium mb-1.5">
                    <Sparkles size={12} /> Sugeridos para esta OS
                    <span className="text-[10px] text-emerald-700/70">
                      ({(formData.cidade_atendimento || 'sem cidade').toString()}{' '}
                      {suggestedColaboradores[0]?.catsCobertas?.length ? '· categoria compatível' : ''})
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestedColaboradores.map(({ c, cover }) => {
                      const isSel = formData.colaborador_id === c.id;
                      return (
                        <button
                          type="button"
                          key={c.id}
                          onClick={() => setFormData((prev) => ({ ...prev, colaborador_id: c.id }))}
                          data-testid={`colab-sug-${c.id}`}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                            isSel
                              ? 'bg-emerald-600 text-white border-emerald-600'
                              : 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-100'
                          }`}
                          title={`Regiões: ${((c.areas_atendimento || []).length > 0
                            ? c.areas_atendimento.map((a) => `${a.cidade}${!a.toda_cidade && (a.zonas || []).length ? ` (${a.zonas.join(', ')})` : ''}`)
                            : (c.regioes_atendimento || [])
                          ).join(', ') || '—'} · Categorias: ${(c.categorias_servico || []).join(', ') || '—'}`}
                        >
                          {c.nome}{cover > 0 && <span className="ml-1 opacity-75">· {cover}✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <ColaboradorCombobox
                colaboradores={colaboradores}
                value={formData.colaborador_id}
                onChange={(v) => setFormData({ ...formData, colaborador_id: v })}
                testId="colaborador-select"
              />
            </div>

            <div className="space-y-2 rounded-lg border p-3 bg-gray-50">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Itens da OS *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addItem}
                  data-testid="add-item-btn"
                >
                  <Plus size={14} className="mr-1" /> Adicionar item
                </Button>
              </div>
              <div className="space-y-2">
                {formData.itens.map((it, idx) => {
                  const sub = Number(it.valor_unitario || 0) * Number(it.quantidade || 1);
                  return (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 rounded-md bg-white border" data-testid={`os-item-${idx}`}>
                      <div className="col-span-12 md:col-span-5 space-y-1">
                        <Label className="text-xs">Serviço</Label>
                        <ServicoPicker
                          servicos={servicos}
                          value={it.servico_id || ''}
                          onChange={(v) => handleItemServicoChange(idx, v)}
                          placeholder="Selecione o serviço"
                          testId={`item-servico-select-${idx}`}
                        />
                      </div>
                      {(() => {
                        const isM2 = servicos.find((s) => s.id === it.servico_id)?.unidade === 'm2';
                        return (
                          <div className="col-span-4 md:col-span-2 space-y-1">
                            <Label className="text-xs">{isM2 ? 'm²' : 'Qtde'}</Label>
                            <Input
                              type="number"
                              min={isM2 ? '0.1' : '1'}
                              step={isM2 ? '0.1' : '1'}
                              value={it.quantidade}
                              onChange={(e) => updateItem(idx, { quantidade: isM2 ? Math.max(0.1, parseFloat(e.target.value) || 0.1) : Math.max(1, parseInt(e.target.value, 10) || 1) })}
                              data-testid={`item-qtd-${idx}`}
                            />
                          </div>
                        );
                      })()}
                      <div className="col-span-5 md:col-span-2 space-y-1">
                        <Label className="text-xs">Vlr unit. (R$)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={it.valor_unitario}
                          onChange={(e) => updateItem(idx, { valor_unitario: parseFloat(e.target.value) || 0 })}
                          data-testid={`item-vunit-${idx}`}
                        />
                      </div>
                      <div className="col-span-3 md:col-span-2 space-y-1">
                        <Label className="text-xs">Subtotal</Label>
                        <div className="h-9 flex items-center px-2 rounded-md bg-gray-100 text-sm font-medium text-emerald-700">
                          {formatCurrency(sub)}
                        </div>
                      </div>
                      <div className="col-span-12 md:col-span-1 flex md:justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(idx)}
                          disabled={formData.itens.length === 1}
                          title="Remover item"
                          data-testid={`item-remove-${idx}`}
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-end pt-1 text-sm">
                <span className="text-gray-500 mr-2">Total:</span>
                <span className="font-bold text-emerald-700 text-lg" data-testid="os-total">
                  {formatCurrency(computeTotal())}
                </span>
              </div>
            </div>

            {/* Valor a pagar ao prestador */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <Label htmlFor="valor_prestador" className="text-amber-900 font-semibold">
                Valor a pagar ao prestador (R$)
              </Label>
              {(() => {
                const colab = colaboradores.find((c) => c.id === formData.colaborador_id);
                const pct = colab ? Number(colab.comissao || 0) : 0;
                const sugerido = pct > 0 ? (computeTotal() * pct / 100) : 0;
                return (
                  <>
                    <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
                      <Input
                        id="valor_prestador"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.valor_prestador}
                        onChange={(e) => {
                          setVpTouched(true);
                          setFormData({ ...formData, valor_prestador: e.target.value });
                        }}
                        placeholder={sugerido > 0 ? formatCurrency(sugerido).replace('R$', '').trim() : '0,00'}
                        className="bg-white"
                        data-testid="valor-prestador-input"
                      />
                      {sugerido > 0 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="whitespace-nowrap"
                          onClick={() => {
                            setVpTouched(false);
                            setFormData({ ...formData, valor_prestador: sugerido.toFixed(2) });
                          }}
                          data-testid="valor-prestador-sugerido"
                        >
                          Voltar ao sugerido ({pct}%): {formatCurrency(sugerido)}
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-amber-800 mt-1">
                      Padrão: {pct}% da OS ({formatCurrency(sugerido)}). Você pode editar. Vai aparecer na mensagem WhatsApp do prestador e ser lançado como Repasse no financeiro ao concluir a OS.
                    </p>
                  </>
                );
              })()}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="data_agendamento">Data *</Label>
                <Input
                  id="data_agendamento"
                  type="date"
                  value={formData.data_agendamento}
                  onChange={(e) => setFormData({ ...formData, data_agendamento: e.target.value })}
                  required
                  data-testid="data-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hora_agendamento">Horário (interno) *</Label>
                <Input
                  id="hora_agendamento"
                  type="time"
                  value={formData.hora_agendamento}
                  onChange={(e) => setFormData({ ...formData, hora_agendamento: e.target.value })}
                  required
                  data-testid="hora-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cidade_atendimento">Cidade</Label>
                <Select
                  value={formData.cidade_atendimento || undefined}
                  onValueChange={(v) => setFormData({ ...formData, cidade_atendimento: v, zona_atendimento: '' })}
                >
                  <SelectTrigger data-testid="os-cidade-select">
                    <SelectValue placeholder="Selecione a cidade" />
                  </SelectTrigger>
                  <SelectContent>
                    <CidadeSelectOptions cidades={cidadesList} currentValue={formData.cidade_atendimento} />
                  </SelectContent>
                </Select>
              </div>
              <ZonaBairroFields
                cidades={cidadesList}
                cidade={formData.cidade_atendimento}
                zona={formData.zona_atendimento}
                bairro={formData.bairro_atendimento}
                onZonaChange={(v) => setFormData({ ...formData, zona_atendimento: v })}
                onBairroChange={(v) => setFormData({ ...formData, bairro_atendimento: v })}
                testPrefix="os"
              />
              <div className="space-y-2">
                <Label htmlFor="periodo_atendimento">Período (mostrado ao cliente)</Label>
                <Select
                  value={formData.periodo_atendimento || undefined}
                  onValueChange={(v) => setFormData({ ...formData, periodo_atendimento: v })}
                >
                  <SelectTrigger data-testid="os-periodo-select">
                    <SelectValue placeholder="Selecione o período" />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODOS_ATENDIMENTO.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label} ({p.horario})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="forma_pagamento">Forma de Pagamento</Label>
                <Select 
                  value={formData.forma_pagamento} 
                  onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}
                >
                  <SelectTrigger data-testid="forma-pagamento-select">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {FORMAS_PAGAMENTO.map((forma) => (
                      <SelectItem key={forma} value={forma}>
                        {forma}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Observações sobre o serviço..."
                rows={3}
                data-testid="observacoes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-ordem-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>
              Detalhes da Ordem de Serviço
              {selectedOrdem?.numero && (
                <span className="ml-2 text-sm font-mono px-2 py-0.5 rounded bg-gray-100 border font-semibold text-gray-700 align-middle">
                  {formatOrdemNumero(selectedOrdem.numero)}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Visualize e gerencie os detalhes desta ordem
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrdem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Cliente</p>
                  <p className="font-medium">{selectedOrdem.cliente_nome}</p>
                  <p className="text-sm text-gray-500">{selectedOrdem.cliente_telefone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Colaborador</p>
                  <p className="font-medium">{selectedOrdem.colaborador_nome}</p>
                  <p className="text-sm text-gray-500">{selectedOrdem.colaborador_telefone}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">Endereço</p>
                <p className="font-medium">{formatEndereco(selectedOrdem.cliente_endereco)}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">Itens</p>
                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="text-left px-3 py-2">Serviço</th>
                        <th className="text-center px-3 py-2 w-16">Qtd</th>
                        <th className="text-right px-3 py-2 w-24">Vlr Unit</th>
                        <th className="text-right px-3 py-2 w-24">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedOrdem.itens || []).map((it, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-2">{it.servico_nome}</td>
                          <td className="text-center px-3 py-2">{it.quantidade}</td>
                          <td className="text-right px-3 py-2">{formatCurrency(it.valor_unitario)}</td>
                          <td className="text-right px-3 py-2 font-medium">{formatCurrency(it.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-gray-50">
                        <td colSpan={3} className="text-right px-3 py-2 font-semibold text-gray-600">Total</td>
                        <td className="text-right px-3 py-2 font-bold text-emerald-700">
                          {formatCurrency(selectedOrdem.valor)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Data/Hora</p>
                  <p className="font-medium">{formatDate(selectedOrdem.data_agendamento)} - {selectedOrdem.hora_agendamento}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Cidade</p>
                  <p className="font-medium">
                    {[selectedOrdem.cidade_atendimento, selectedOrdem.zona_atendimento, selectedOrdem.bairro_atendimento]
                      .filter(Boolean).join(' • ') || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Período</p>
                  <p className="font-medium">{selectedOrdem.periodo_atendimento || '—'}</p>
                </div>
              </div>
              {selectedOrdem.valor_prestador != null && Number(selectedOrdem.valor_prestador) > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
                  <span className="font-medium">Valor a pagar ao prestador:</span>{' '}
                  <span className="font-bold">{formatCurrency(selectedOrdem.valor_prestador)}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Forma de Pagamento</p>
                  <p className="font-medium">{selectedOrdem.forma_pagamento || 'Não informada'}</p>
                </div>
              </div>

              {selectedOrdem.observacoes && (
                <div>
                  <p className="text-sm text-gray-500">Observações</p>
                  <p className="font-medium">{selectedOrdem.observacoes}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500 mb-2">Status</p>
                <Select 
                  value={selectedOrdem.status} 
                  onValueChange={(value) => {
                    handleUpdateStatus(selectedOrdem.id, value);
                    setSelectedOrdem({ ...selectedOrdem, status: value });
                  }}
                >
                  <SelectTrigger data-testid="detail-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleCopyWhatsApp(selectedOrdem, 'cliente')}
                  data-testid="copy-cliente-btn"
                >
                  <Copy size={16} className="mr-2" />
                  p/ Cliente
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleCopyWhatsApp(selectedOrdem, 'colaborador')}
                  data-testid="copy-colaborador-btn"
                >
                  <Copy size={16} className="mr-2" />
                  p/ Colaborador
                </Button>
              </div>

              <Button
                variant="outline"
                className="w-full border-emerald-300 text-[#2B7A3D] hover:bg-emerald-50"
                onClick={() => handleCopyWhatsApp(selectedOrdem, 'consulta')}
                data-testid="copy-consulta-prestador-btn"
              >
                <Copy size={16} className="mr-2" />
                Consultar parceiro (disponibilidade)
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => baixarRecibo(selectedOrdem)}
                data-testid="recibo-pdf-btn"
              >
                <FileDown size={16} className="mr-2" />
                Recibo (PDF)
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowDetails(false);
                    handleOpenForm(selectedOrdem);
                  }}
                  data-testid="edit-from-detail-btn"
                >
                  <Pencil size={16} className="mr-2" />
                  Editar
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => {
                    setShowDetails(false);
                    setShowDeleteDialog(true);
                  }}
                  data-testid="delete-from-detail-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  Excluir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta ordem de serviço? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Agendamento;
